import type { TAnimalDetailsByIdUi } from 'data/types/animal.types';
import type { TInitialValues } from './AnimalForm.types';

/**
 * Its own module, with no component imports, so it can be unit-tested without dragging the UI kit
 * and antd's ESM build into the test runner.
 */
export const initialValues: TInitialValues = {
  profilePicture: '',
  name: '',
  birthDate: '',
  species: null,
  breed: null,
  weight: '',
  primaryColor: null,
  secondaryColor1: null,
  secondaryColor2: null,
  gender: null,
  houseTrained: null,
  neutered: null,
};

/**
 * What `AnimalForm` hands rc-field-form as `initialValues`.
 *
 * <p>The one mapping in here is load-bearing and was the subject of PP-9478's twice-shipped data
 * loss. `animalData` carries the record nested under `aggressiveBehavior`, but the form registers
 * `isAggressive` and `aggressionInfo`. When the property hides History of Aggression those two
 * fields are unmounted and therefore absent from `validateFields()`, so
 * `CreateAnimalModal.handleSubmit` recovers them from `getFieldsValue(true)` — which only finds
 * them if they are in the store under the names the form uses. Without this flattening they are in
 * no store at all: completing such an animal sent `acted: undefined`,
 * `AggressiveBehaviorDeserializer` returned null for the *whole* value object, and
 * `AnimalAggregate.updateInternal` assigned it unconditionally, so the incident text was erased
 * along with the flag — with no control on that form to restore it.
 *
 * <p>It has to be seeded HERE, at the `initialValues` boundary, and not through `setFieldsValue`.
 * `CreateAnimalForm` calls `form.resetFields()` on `useGetAnimalByIdQuery` success, which is
 * pre-submit, and rc-field-form's argument-less `resetFields()` replaces the whole store with
 * `initialValues`. So an effect-based seed is wiped before the resident submits, and a
 * render-body seed clobbers the resident's own edits on any unrelated re-render
 * (`setAnimalType` -> `useGetAnimalBreedsQuery` is a real one). Values that live in
 * `initialValues` are part of what a reset resets *to*, which is the only placement immune to
 * both. `animalFormInitialValues.test.tsx` pins all three properties.
 *
 * <p>`acted` is already 'Yes'/'No' by this point — `aggressiveBehaviorConverter` converts it — so
 * it needs no further mapping.
 */
export const animalFormInitialValues = (
  animalData?: TAnimalDetailsByIdUi,
): Record<string, unknown> | TInitialValues =>
  animalData
    ? {
        ...animalData,
        isAggressive: animalData.aggressiveBehavior?.acted,
        aggressionInfo: animalData.aggressiveBehavior?.incidentDetails,
      }
    : initialValues;
